
import React from "react";
import { Capability, Schemes, Utils } from "../../../../../utils";
import { i18nManager } from "../../../../../i18n";
import * as ReactRouterDom from "react-router-dom";
import { Elements } from "../../../../shared/Elements";
import { Button, ButtonManageRef, Classname, Dropdown, FormControl, Scheme, TextInput, InputManageRef, useScreenDimension } from "@rivtn/noseur";
import { BaseService, IMember, IHost, IHostManager, SanitizedResponse, HostRole, HostService } from "../../../../../services";
import { ConfirmDialog, Siren } from "../../../../shared/ConfirmDialog";
import UpSellPlan from "../../../../shared/UpSellPlan";

export interface ManagersProps {
    host: IHost;
    profile: IMember;
}

function Managers(props: ManagersProps) {

    const { host, profile } = props;
    const { width } = useScreenDimension();
    const hostService = HostService.getInstance();
    const labels = i18nManager.Labels.host.settings.general.managers;
    const inviteeNameInputManageRef = React.useRef<InputManageRef>(null);
    const [isHostOwner, setIsHostOwner] = React.useState<boolean>(false);
    const [inviteeUsername, setInviteeUsername] = React.useState<string>();
    const [hostManagers, setHostManagers] = React.useState<IHostManager[]>();

    React.useEffect(fetchInformation, []);

    return (<div style={{ marginTop: 10, display: "flex", flexDirection: "column" }}>
        <UpSellPlan type="icon" capability={Capability.HOST_OWNERSHIP_TRANSFER} childrenRelayProps={{ style: { opacity: 0.7 } }} >
            <span className={Classname.build("link danger", { "noseur-disabled": !isHostOwner })} style={{ margin: "5px 0px" }} onClick={transferOwnership}>{labels.transfer_host_ownership}</span>
        </UpSellPlan>
        <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
            <FormControl isFieldset scheme={Schemes.OMNEHOST} style={{ marginTop: 15, width: (width > 800 ? 350 : undefined), flex: (width > 800 ? undefined : 1) }} label={labels.enter_user_id}>
                <TextInput manageRef={inviteeNameInputManageRef} fill onInput={(e: any) => setInviteeUsername(e.target.value)} />
            </FormControl>
            <UpSellPlan capability={Capability.MAX_HOST_MANAGER} currentValue={hostManagers?.length} childrenRelayProps={Elements.BUTTON_UPGRADE_PROPS} >
                <Button raised scheme={Schemes.OMNEHOST} disabled={!inviteeUsername} text={labels.send_invite} style={{ marginLeft: 25, marginTop: 20 }} onClick={() => inviteUserAsManager()} />
            </UpSellPlan>
        </div>
        <div className="team-mods">
            {!hostManagers?.length && (Elements.LOADING_STATE)}
            {hostManagers?.map((hostManager) => {
                const isOwner = hostManager.role === HostRole.OWNER;
                const isYou = hostManager.member?.external_id === profile.external_id && (i18nManager.Labels.host.analytics.earnings.you);

                return (<div className="team-mod" key={hostManager.external_id}>
                    <UpSellPlan capability={Capability.MANAGE_HOST_MANAGERS} childrenRelayProps={{ style: { opacity: 0.7 } }} >
                        <i style={{ marginTop: 10 }} className={Classname.build("fa fa-times link danger", { "noseur-disabled": isOwner })} onClick={() => removeHostManager(hostManager)} />
                    </UpSellPlan>
                    <img alt={hostManager.member?.username} src={hostManager.member?.profile_picture_url} />
                    <div className="details">
                        <span className="n">
                            {hostManager.member?.username}{" "}
                            {(isYou || isOwner) && `(${isOwner ? i18nManager.Labels.host.analytics.earnings.owner + (isYou ? ", " : "") : ""}${isYou ? i18nManager.Labels.host.analytics.earnings.you : ""})`}
                        </span>
                        <span className="e">{hostManager.member?.user_detail?.email}</span>
                        <span className="m">{labels.manager_since} {Utils.formatToFineDate(hostManager.join_date, true, true)}</span>
                    </div>
                </div>);
            })}
        </div>
    </div>);

    function fetchInformation() {
        hostService.queryHostManagers(host.external_id, { status: "ACTIVE" }).then(({ content }: SanitizedResponse<IHostManager>) => {
            setHostManagers(content);
            for (const member of content) {
                if (member.role === HostRole.OWNER && member.member?.external_id === profile.external_id) {
                    setIsHostOwner(true);
                    break;
                }
            }
        }).catch(BaseService.reportError);
    }

    function transferOwnership() {
        let tournamentHostName = "";
        let selectedManager: IHostManager;
        const dialogLabels = labels.dialog.transfer;
        const dialogErrorRef = { current: null } as any;

        const dialog = ConfirmDialog({
            closable: true,
            title: dialogLabels.title,
            confirmScheme: Scheme.DANGER,
            confirmLabel: dialogLabels.title,
            desc: dialogLabels.desc.replace("${host_name}", host.name),
            content: (<div className="dialog-content" style={{ marginTop: 10 }}>
                <FormControl isFieldset fill scheme={Schemes.OMNEHOST} style={{ marginTop: 15 }} label={dialogLabels.search_select_man}>
                    <Dropdown fill options={hostManagers} optionMap={{
                        label: "display_name",
                        icon: "passport_picture_url",
                    }} onSelectOption={(o: IHostManager) => {
                        console.log("WE SELECTED", o);
                        selectedManager = o;
                    }} />
                </FormControl>
                <FormControl isFieldset fill label={dialogLabels.host_name} style={{ marginTop: 15 }}>
                    <TextInput onInputComplete={(v) => {
                        dialogErrorRef.current!.innerText = " ";
                        tournamentHostName = v;
                    }} />
                </FormControl>
                <div ref={dialogErrorRef} className="error" style={{ marginBottom: 10 }}> </div>
            </div>),
            onConfirm: async (button?: ButtonManageRef) => {
                if (tournamentHostName !== host.name) {
                    dialogErrorRef.current!.innerText = i18nManager.Labels.host.dialog.confirm_host.name_does_not_match;
                    return false;
                }
                button?.setLoadingState(true);
                hostService.transferOwnership(selectedManager?.external_id).then(({ apiResponse }: SanitizedResponse<any>) => {
                    dialog.hide(() => {
                        inviteeNameInputManageRef.current?.clear();
                        Siren.alert(dialogLabels.ownership_transfer_successful);
                        fetchInformation();
                    });
                }).catch(({ errorMessage }: any) => {
                    dialogErrorRef.current!.innerText = errorMessage;
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

    function inviteUserAsManager() {
        let tournamentHostName = "";
        if (!inviteeUsername) return;
        const dialogLabels = labels.dialog.invite;
        const dialogErrorRef = { current: null } as any;

        const dialog = ConfirmDialog({
            closable: true,
            title: dialogLabels.title,
            confirmLabel: dialogLabels.send_invite,
            desc: dialogLabels.desc.replace("${host_name}", host.name).replace("${username}", inviteeUsername).replace("${userid}", "").replace("${host_name}", host.name),
            content: (<div className="dialog-content" style={{ marginTop: 10 }}>
                <FormControl isFieldset fill label={dialogLabels.host_name} style={{ marginTop: 15 }}>
                    <TextInput onInputComplete={(v) => {
                        dialogErrorRef.current!.innerText = " ";
                        tournamentHostName = v;
                    }} />
                </FormControl>
                <div ref={dialogErrorRef} className="error" style={{ marginBottom: 10 }}> </div>
            </div>),
            onConfirm: async (button?: ButtonManageRef) => {
                if (tournamentHostName !== host.name) {
                    dialogErrorRef.current!.innerText = i18nManager.Labels.host.dialog.confirm_host.name_does_not_match;
                    return false;
                }
                button?.setLoadingState(true);
                hostService.inviteManager(host.external_id, inviteeUsername).then(({ apiResponse }: SanitizedResponse<any>) => {
                    dialog.hide(() => {
                        inviteeNameInputManageRef.current?.clear();
                        Siren.alert(apiResponse.message);
                    });
                }).catch(({ errorMessage }: any) => {
                    dialogErrorRef.current!.innerText = errorMessage;
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

    function removeHostManager(hostManager: IHostManager) {
        let tournamentHostName = "";
        const dialogLabels = labels.dialog.remove;
        const dialogErrorRef = { current: null } as any;

        const dialog = ConfirmDialog({
            closable: true,
            title: dialogLabels.title,
            confirmScheme: Scheme.DANGER,
            confirmLabel: dialogLabels.remove,
            desc: dialogLabels.desc.replace("${host_name}", host.name).replace("${manager_name}", hostManager.member?.username ?? ""),
            content: (<div className="dialog-content" style={{ marginTop: 10 }}>
                <FormControl isFieldset fill label={dialogLabels.man_name} style={{ marginTop: 15 }} scheme={Schemes.OMNEHOST}>
                    <TextInput scheme={Schemes.OMNEHOST} onInputComplete={(v) => {
                        dialogErrorRef.current!.innerText = " ";
                        tournamentHostName = v;
                    }} />
                </FormControl>
                <div ref={dialogErrorRef} className="error" style={{ marginBottom: 10 }}> </div>
            </div>),
            onConfirm: async (button?: ButtonManageRef) => {
                if (tournamentHostName !== host.name) {
                    dialogErrorRef.current!.innerText = i18nManager.Labels.host.dialog.confirm_host.name_does_not_match;
                    return false;
                }
                button?.setLoadingState(true);
                hostService.removeManager(hostManager.external_id).then(({ apiResponse }: SanitizedResponse<any>) => {
                    dialog.hide(() => {
                        fetchInformation();
                        Siren.alert(dialogLabels.remove_successful);
                    });
                }).catch(({ errorMessage }: any) => {
                    dialogErrorRef.current!.innerText = errorMessage;
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

}

export default Managers;
